import { Connection, type ConnectionConfig } from '@solana/web3.js';
import { isLeft } from 'fp-ts/Either';

import { captureAndReportError } from './errorReporting';
import { normalizeMetricKey, trackCount, trackDuration } from './sentryMetrics';

const RPC_PROXY_REQUEST_COUNT_METRIC = 'portal.web.rpc.proxy.request';
const RPC_PROXY_REQUEST_DURATION_METRIC =
  'portal.web.rpc.proxy.request.duration';

interface JsonRpcRequestPayload {
  jsonrpc: '2.0';
  id: string;
  method: string;
  params: unknown[];
}

type RawRpcResponse<T = unknown> = {
  jsonrpc?: '2.0';
  id?: number | string;
  result?: T;
  error?: {
    code: number;
    message: string;
    data?: unknown;
  };
};

type EitherResponse<T> =
  | { _tag: 'Left'; left: { message: string; name?: string } }
  | { _tag: 'Right'; right: T };

type BackendProxyResponse<T = unknown> =
  | EitherResponse<RawRpcResponse<T>>
  | RawRpcResponse<T>;

type JsonRpcBatchRequestPayload = Array<{
  methodName: string;
  args: unknown[];
}>;

export const resolveProxyEndpoint = (url: string) => {
  if (url.startsWith('http://') || url.startsWith('https://')) {
    return url;
  }

  if (typeof window !== 'undefined') {
    try {
      return new URL(url, window.location.origin).toString();
    } catch {
      // fall through
    }
  }

  return url;
};

function toEndpointLabel(endpoint: string): string {
  try {
    return normalizeMetricKey(new URL(endpoint).hostname);
  } catch {
    return normalizeMetricKey(endpoint);
  }
}

export class BackendProxyConnection extends Connection {
  private trpcProxy: (
    request: JsonRpcRequestPayload,
  ) => Promise<BackendProxyResponse>;
  private readonly customRpcEndpoint: string;
  private readonly endpointLabel: string;
  private requestCounter = 0;

  constructor(
    endpoint: string,
    trpcProxy: (
      request: JsonRpcRequestPayload,
    ) => Promise<BackendProxyResponse>,
    commitmentOrConfig?: ConnectionConfig | string,
  ) {
    super(endpoint, commitmentOrConfig as ConnectionConfig);

    this.customRpcEndpoint = endpoint;
    this.endpointLabel = toEndpointLabel(endpoint);
    this.trpcProxy = trpcProxy;

    // `Connection` installs instance-level RPC handlers in its constructor,
    // so a prototype method override is ignored unless we replace them here.
    const connection = this as unknown as {
      _rpcRequest: (method: string, args: unknown[]) => Promise<unknown>;
      _rpcBatchRequest: (
        requests: JsonRpcBatchRequestPayload,
      ) => Promise<unknown[]>;
    };
    connection._rpcRequest = this.proxyRpcRequest;
    connection._rpcBatchRequest = async (requests) =>
      Promise.all(
        requests.map(({ methodName, args }) =>
          this.proxyRpcRequest(methodName, args),
        ),
      );
  }

  get rpcEndpoint(): string {
    return this.customRpcEndpoint;
  }

  private proxyRpcRequest = async (
    method: string,
    args: unknown[],
  ): Promise<unknown> => {
    const start =
      typeof performance !== 'undefined' ? performance.now() : Date.now();
    const methodLabel = normalizeMetricKey(method);
    let outcome: 'success' | 'failure' = 'success';
    let errorName: string | undefined;

    try {
      const request: JsonRpcRequestPayload = {
        jsonrpc: '2.0',
        id: `${Date.now()}-${++this.requestCounter}`,
        method,
        params: args ?? [],
      };

      const response = await this.trpcProxy(request);

      const normalizeResponse = (
        resp: BackendProxyResponse,
      ): RawRpcResponse => {
        if (resp && typeof resp === 'object' && '_tag' in resp) {
          if (isLeft(resp)) {
            throw new Error(resp.left.message || 'RPC request failed');
          }
          return resp.right as RawRpcResponse;
        }
        return resp as RawRpcResponse;
      };

      const normalized = normalizeResponse(response);

      if (normalized && typeof normalized === 'object') {
        return normalized;
      }

      throw new Error('Unexpected response format from backend proxy');
    } catch (error: unknown) {
      outcome = 'failure';
      errorName = normalizeMetricKey(
        error instanceof Error ? error.name : 'unknown',
      );
      captureAndReportError(
        '[BackendProxyConnection] RPC proxy error:',
        error,
        {
          method,
        },
      );
      if (error instanceof Error) {
        throw error;
      }
      throw new Error(String(error));
    } finally {
      const durationMs =
        (typeof performance !== 'undefined' ? performance.now() : Date.now()) -
        start;

      const attributes: Record<string, string | number | boolean> = {
        method: methodLabel,
        endpoint: this.endpointLabel,
        outcome,
      };

      if (errorName) {
        attributes.error_name = errorName;
      }

      trackCount(RPC_PROXY_REQUEST_COUNT_METRIC, 1, attributes);
      trackDuration(RPC_PROXY_REQUEST_DURATION_METRIC, durationMs, attributes);
    }
  };
}
